import { useEffect, useState } from "react";
import { announcementAPI, lookupAPI, reportAPI } from "../services/api";
import { Modal, Btn, Th, Td, ActionBtns, ThActions, ConfirmDialog, Toast, Can, errMessage, TableScroll, Pagination } from "../components/shared/index";
import { useLanguage } from "../context/LanguageContext";
import { MdRefresh, MdCheckCircle, MdHourglassEmpty, MdEdit, MdDelete } from "react-icons/md";

const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const STATUS_COLOR_MAP = {
  DRAFT:"#F59E0B", PUBLISHED:"#10B981", SCHEDULED:"#3B82F6",
  ARCHIVED:"#6B7280", CANCELLED:"#EF4444",
};
// the API returns `statusCode` (+ a ready-made `statusLabel`); older payloads used `status`
const annStatus = (a) => a?.statusCode ?? a?.status ?? "";
const getAnnLabel = (statuses, code) =>
  statuses.find(s => s.statusCode === code)?.labelKh || code || "—";
const getAnnColor = (code) => STATUS_COLOR_MAP[code] || "#94A3B8";
const PRIORITY_LABEL = { LOW:"ទាប", MEDIUM:"មធ្យម", HIGH:"ខ្ពស់", URGENT:"បន្ទាន់" };
const PRIORITY_COLOR = { LOW:"#10B981", MEDIUM:"#3B82F6", HIGH:"#F59E0B", URGENT:"#EF4444" };

const inputStyle = {
  width:"100%", padding:"9px 12px", border:`1px solid ${BORDER}`,
  borderRadius:8, fontSize:13, outline:"none", background:"var(--surface)",
  fontFamily:KH, boxSizing:"border-box",
};

const toDisplay = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB"); } catch { return iso; }
};

function SkeletonRow({ cols=6 }) {
  return (
    <tr>
      {Array(cols).fill(0).map((_,i) => (
        <td key={i} style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
          <div style={{ height:14, background:"var(--border)", borderRadius:4 }} />
        </td>
      ))}
    </tr>
  );
}

function PriorityBadge({ p, label }) {
  const c = PRIORITY_COLOR[p] || "#6B7280";
  return (
    <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:600,
                   background:c+"22", color:c, fontFamily:KH }}>
      {label || PRIORITY_LABEL[p] || p || "—"}
    </span>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display:"flex", gap:10, padding:"9px 0", borderBottom:`1px solid ${BORDER}` }}>
      <span style={{ fontSize:12, color:MUTED, minWidth:160, fontFamily:KH, flexShrink:0 }}>{label}</span>
      <span style={{ fontSize:13, color:"var(--text)", fontFamily:KH, flex:1 }}>{value || "—"}</span>
    </div>
  );
}

function AnnouncementList() {
  const { t } = useLanguage();
  const [list,       setList]       = useState([]);
  const [annStatuses,setAnnStatuses]= useState([]);
  const [loading,    setLoading]    = useState(true);
  const [statusFil,  setStatusFil]  = useState("");
  const [modal,      setModal]      = useState(null);
  const [form,       setForm]       = useState({});
  const [selected,   setSelected]   = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [page,       setPage]       = useState(0);
  const [pageSize,   setPageSize]   = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteId,   setDeleteId]   = useState(null);
  const [toast,      setToast]      = useState(null);
  const [err,        setErr]        = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res  = await announcementAPI.getAll(page, pageSize, statusFil || undefined);
      const data = res?.data?.data ?? res?.data;
      setList(data?.content || []);
      setTotalPages(data?.totalPages    || 1);
      setTotalItems(data?.totalElements || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    lookupAPI.getAnnouncementStatuses().then(r => {
      const d = r?.data?.data ?? r?.data;
      setAnnStatuses(Array.isArray(d) ? d : []);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [page, pageSize, statusFil]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title?.trim()) { setErr(t("សូមបញ្ចូលចំណងជើង")); return; }
    setSaving(true);
    try {
      const payload = {
        title:      form.title     || "",
        content:    form.content   || "",
        publishAt:  form.publishAt || undefined,
        statusCode: form.status    || "DRAFT",
        priority:   form.priority  || "MEDIUM",
      };
      if (editId) {
        await announcementAPI.update(editId, payload);
      } else {
        await announcementAPI.create(payload);
      }
      await load();
      setToast(editId ? "កែប្រែការប្រកាសបានជោគជ័យ!" : "បន្ថែមការប្រកាសបានជោគជ័យ!");
      setModal(null); setForm({}); setEditId(null);
    } catch (e) { setErr(errMessage(e)); }
    finally { setSaving(false); }
  };

  const handleDelete = (id) => setDeleteId(id);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await announcementAPI.delete(id);
      await load();
      if ((selected?.announcementId ?? selected?.id) === id) setModal(null);
      setToast(t("លុបការប្រកាសបានជោគជ័យ!"));
    } catch (e) { setErr(errMessage(e)); }
  };

  // Opening an announcement counts as reading it — POST …/mark-read records that
  // for the current JWT user, which is what feeds readCount on /read-status.
  const openView = async (a) => {
    setSelected(a);
    setModal("view");
    if (a.currentUserRead) return;
    try {
      await announcementAPI.markAsRead(a.announcementId ?? a.id);
      const aid = a.announcementId ?? a.id;
      setList(prev => prev.map(x =>
        (x.announcementId ?? x.id) === aid ? { ...x, currentUserRead:true } : x
      ));
      setSelected(prev => prev ? { ...prev, currentUserRead:true } : prev);
    } catch (e) {
      // not being a recipient is a normal outcome for an admin — don't nag
      console.warn("mark-read failed:", errMessage(e));
    }
  };

  const openEdit = (a) => {
    setEditId(a.announcementId ?? a.id);
    setForm({
      title:     a.title || "",
      content:   a.content || "",
      publishAt: a.publishAt?.slice(0,16) || "",
      status:    annStatus(a) || "DRAFT",
      priority:  a.priority || "MEDIUM",
    });
    setModal("add");
  };

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
        {[
          { label:"ការប្រកាសសរុប",   value:totalItems,                                       color:BROWN   },
          { label:"សេចក្តីព្រាង",    value:list.filter(a=>annStatus(a)==="DRAFT").length,      color:"#F59E0B" },
          { label:"បានផ្សព្វផ្សាយ",  value:list.filter(a=>annStatus(a)==="PUBLISHED").length,  color:"#10B981" },
          { label:"បោះបង់",           value:list.filter(a=>annStatus(a)==="CANCELLED").length, color:"#EF4444" },
        ].map((s,i) => (
          <div key={i} style={{ background:"var(--surface)", borderRadius:10, padding:"14px 18px",
                                 border:`1px solid ${BORDER}` }}>
            <p style={{ margin:"0 0 6px", fontSize:12, color:MUTED, fontFamily:KH }}>{s.label}</p>
            <p style={{ margin:0, fontSize:26, fontWeight:700, color:s.color, fontFamily:MO }}>
              {loading ? "..." : s.value}
            </p>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center" }}>
        <select value={statusFil} onChange={e => { setStatusFil(e.target.value); setPage(0); }}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("ស្ថានភាពទាំងអស់")}</option>
          {annStatuses.length > 0
            ? annStatuses.map(s => (
                <option key={s.statusCode} value={s.statusCode}>
                  {s.labelKh || s.labelEn || s.statusCode}
                </option>
              ))
            : ["DRAFT","PUBLISHED","SCHEDULED","ARCHIVED","CANCELLED"].map(s => (
                <option key={s} value={s}>{s}</option>
              ))
          }
        </select>
        <div style={{ flex:1 }} />
        <Can perm="announcement" action="create">
        <button onClick={() => { setEditId(null); setForm({ status:"DRAFT", priority:"MEDIUM" }); setModal("add"); }}
          style={{ padding:"9px 18px", background:BROWN, color:"#fff", border:"none",
                   borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:500, fontFamily:KH }}>
          {t("+ បន្ថែមការប្រកាស")}
        </button>
        </Can>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10, border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("ចំណងជើង")}</Th>
              <Th>{t("ស្ថានភាព")}</Th>
              <Th>{t("អាទិភាព")}</Th>
              <Th>{t("ថ្ងៃផ្សព្វផ្សាយ")}</Th>
              <Th>{t("បង្កើតដោយ")}</Th>
              <Th>{t("ថ្ងៃបង្កើត")}</Th>
              <ThActions perm="announcement" />
            </tr>
          </thead>
          <tbody>
            {loading ? [1,2,3].map(i => <SkeletonRow key={i} cols={7} />)
            : list.length === 0 ? (
              <tr><td colSpan={7} style={{ padding:"32px", textAlign:"center", color:MUTED, fontFamily:KH }}>
                {t("គ្មានការប្រកាស")}
              </td></tr>
            ) : list.map(a => {
              const aid = a.announcementId ?? a.id;
              const sc  = getAnnColor(annStatus(a));
              return (
                <tr key={aid}
                  onMouseEnter={e => e.currentTarget.style.background=BG}
                  onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                  <td style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
                    <p style={{ fontSize:13, fontWeight:500, color:"var(--text)", fontFamily:KH }}>
                      {a.title || "—"}
                    </p>
                    {a.content && (
                      <p style={{ fontSize:11, color:MUTED, fontFamily:KH, marginTop:2 }}>
                        {a.content.length>60 ? a.content.slice(0,60)+"..." : a.content}
                      </p>
                    )}
                  </td>
                  <Td>
                    <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:600,
                                   background:sc+"22", color:sc, fontFamily:KH }}>
                      {a.statusLabel || getAnnLabel(annStatuses, annStatus(a))}
                    </span>
                  </Td>
                  <Td><PriorityBadge p={a.priority} label={a.priorityLabel} /></Td>
                  <Td muted>{toDisplay(a.publishAt)}</Td>
                  <Td muted>{a.createdByName || a.createdBy || "—"}</Td>
                  <Td muted>{toDisplay(a.createdAt)}</Td>
                  <ActionBtns perm="announcement"
                    onView={() => openView(a)}
                    onEdit={() => openEdit(a)}
                    onDelete={() => handleDelete(aid)}
                  />
                </tr>
              );
            })}
          </tbody>
        </TableScroll>
        <Pagination
          page={page} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        message={t("តើអ្នកពិតជាចង់លុបការប្រកាសនេះមែនទេ?")}
        onConfirm={doDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {modal === "add" && (
        <Modal key={editId||"new"} title={editId?"កែប្រែការប្រកាស":"បន្ថែមការប្រកាស"}
          onClose={() => { setModal(null); setForm({}); setEditId(null); }}>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:12, color:MUTED, marginBottom:4, fontFamily:KH }}>
              {t("ចំណងជើង *")}
            </label>
            <input value={form.title||""} onChange={e => f("title",e.target.value)}
              placeholder={t("ចំណងជើង...")} style={inputStyle} />
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:12, color:MUTED, marginBottom:4, fontFamily:KH }}>
              {t("ខ្លឹមសារ")}
            </label>
            <textarea value={form.content||""} onChange={e => f("content",e.target.value)}
              rows={4} placeholder={t("ខ្លឹមសារ...")} style={{ ...inputStyle, resize:"vertical" }} />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED, marginBottom:4, fontFamily:KH }}>
                {t("ស្ថានភាព")}
              </label>
              <select value={form.status||"DRAFT"} onChange={e => f("status",e.target.value)} style={inputStyle}>
                {annStatuses.length > 0
            ? annStatuses.map(s => (
                <option key={s.statusCode} value={s.statusCode}>
                  {s.labelKh || s.labelEn || s.statusCode}
                </option>
              ))
            : ["DRAFT","PUBLISHED","SCHEDULED","ARCHIVED","CANCELLED"].map(s => (
                <option key={s} value={s}>{s}</option>
              ))
          }
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED, marginBottom:4, fontFamily:KH }}>
                {t("អាទិភាព")}
              </label>
              <select value={form.priority||"MEDIUM"} onChange={e => f("priority",e.target.value)} style={inputStyle}>
                {Object.entries(PRIORITY_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED, marginBottom:4, fontFamily:KH }}>
                {t("ថ្ងៃផ្សព្វផ្សាយ")}
              </label>
              <input type="datetime-local" value={form.publishAt||""}
                onChange={e => f("publishAt",e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20 }}>
            <Btn variant="outline" onClick={() => { setModal(null); setForm({}); setEditId(null); }}>{t("បោះបង់")}</Btn>
            <Btn onClick={handleSave} style={{ opacity:saving?0.7:1 }}>
              {saving?"កំពុង...":"រក្សាទុក"}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "view" && selected && (
        <Modal title={selected.title||"ការប្រកាស"}
          subtitle={<span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:600,
                                   background:getAnnColor(annStatus(selected))+"22",
                                   color:getAnnColor(annStatus(selected)), fontFamily:KH }}>
            {selected.statusLabel || getAnnLabel(annStatuses, annStatus(selected))}
          </span>}
          onClose={() => setModal(null)}>
          <InfoRow label={t("ចំណងជើង")}        value={selected.title} />
          <InfoRow label={t("ខ្លឹមសារ")}         value={selected.content} />
          <InfoRow label={t("អាទិភាព")}         value={<PriorityBadge p={selected.priority} label={selected.priorityLabel} />} />
          <InfoRow label={t("ថ្ងៃផ្សព្វផ្សាយ")}  value={toDisplay(selected.publishAt)} />
          <InfoRow label={t("ថ្ងៃផុតកំណត់")}     value={selected.expireAt ? toDisplay(selected.expireAt) : "—"} />
          <InfoRow label={t("បង្កើតដោយ")}       value={selected.createdByName} />
          <InfoRow label={t("អ្នកបានអាន")}      value={
            selected.currentUserRead
              ? (selected.currentUserReadAt ? `បាទ/ចាស · ${toDisplay(selected.currentUserReadAt)}` : "បាទ/ចាស")
              : "មិនទាន់"
          } />
          <InfoRow label={t("អ្នកទទួលសរុប")}    value={selected.totalRecipients} />
          <InfoRow label={t("បានអាន")}          value={
            selected.totalRecipients != null
              ? `${selected.readCount ?? 0} / ${selected.totalRecipients}`
              : selected.readCount
          } />
          <InfoRow label={t("ថ្ងៃបង្កើត")}       value={toDisplay(selected.createdAt)} />
          <InfoRow label={t("ថ្ងៃកែប្រែ")}        value={toDisplay(selected.updatedAt)} />
          <div style={{ marginTop:20, display:"flex", justifyContent:"space-between" }}>
            <Can perm="announcement" action="edit">
              <Btn variant="outline" onClick={() => openEdit(selected)} style={{display:"flex",alignItems:"center",gap:5}}>
                <MdEdit size={14}/> {t("កែប្រែ")}
              </Btn>
            </Can>
            <Can perm="announcement" action="delete">
              <Btn variant="danger" onClick={() => handleDelete(selected.announcementId??selected.id)}
                style={{display:"flex",alignItems:"center",gap:5}}>
                <MdDelete size={14}/> {t("លុប")}
              </Btn>
            </Can>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Recipients() {
  const { t } = useLanguage();
  const [announcements, setAnnouncements] = useState([]);
  const [selAnn,        setSelAnn]        = useState("");
  const [recipients,    setRecipients]    = useState([]);
  const [summary,       setSummary]       = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [err,           setErr]           = useState(null);

  useEffect(() => {
    announcementAPI.getAll(0, 100).then(r => {
      const d = r?.data?.data ?? r?.data;
      setAnnouncements(d?.content || []);
    }).catch(() => {});
  }, []);

  const loadRecipients = async (annId) => {
    if (!annId) return;
    setLoading(true);
    try {
      const res  = await announcementAPI.getReadStatus(annId);
      const data = res?.data?.data ?? res?.data;
      const list = Array.isArray(data) ? data
        : data?.recipients || data?.content || [];
      setSummary(Array.isArray(data) ? null : data);

      if (Array.isArray(list) && list.length > 0) {
        setRecipients(list);
      } else {
        // read-status often answers `recipients: null`; the report endpoint carries the names
        const rep  = await reportAPI.previewAnnouncementRecipients(annId).catch(() => null);
        const rows = rep?.data?.data ?? rep?.data;
        setRecipients(Array.isArray(rows) ? rows : []);
      }
    } catch (e) {
      setRecipients([]); setSummary(null); setErr(errMessage(e));
    }
    finally { setLoading(false); }
  };

  const selectedAnn = announcements.find(a =>
    String(a.announcementId ?? a.id) === selAnn
  );

  // the API may answer with totals only (recipients: null) — fall back to the rows when it does send them
  const total    = summary?.totalRecipients ?? recipients.length;
  const read     = summary?.readCount   ?? recipients.filter(r => r.isRead).length;
  const unread   = summary?.unreadCount ?? Math.max(total - read, 0);
  const readRate = total > 0 ? Math.round((read / total) * 100) : 0;

  return (
    <div>
      <Toast message={err} type="error" onHide={() => setErr(null)} />
      <div style={{ background:"var(--surface)", borderRadius:10, border:`1px solid ${BORDER}`,
                    padding:20, marginBottom:20 }}>
        <p style={{ fontSize:12, fontWeight:600, color:MUTED, marginBottom:8, fontFamily:KH }}>
          {t("ជ្រើសសេចក្តីប្រកាស")}
        </p>
        <div style={{ display:"flex", gap:12, alignItems:"center" }}>
          <select value={selAnn} onChange={e => {
              setSelAnn(e.target.value);
              setRecipients([]);
              if (e.target.value) loadRecipients(e.target.value);
            }}
            style={{ flex:1, padding:"9px 12px", border:`1px solid ${BORDER}`,
                     borderRadius:8, fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
            <option value="">{t("ជ្រើសសេចក្តីប្រកាស...")}</option>
            {announcements.map(a => (
              <option key={a.announcementId??a.id} value={String(a.announcementId??a.id)}>
                {a.title || `ការប្រកាស #${a.announcementId??a.id}`}
              </option>
            ))}
          </select>
          {selAnn && (
            <button onClick={() => loadRecipients(selAnn)}
              style={{ padding:"9px 16px", background:BG, border:`1px solid ${BORDER}`,
                       borderRadius:8, cursor:"pointer", fontSize:13, color:BROWN, fontFamily:KH,
                       display:"flex", alignItems:"center", gap:6 }}>
              <MdRefresh size={16}/> {t("ផ្ទុកឡើងវិញ")}
            </button>
          )}
        </div>
        {selectedAnn && (
          <div style={{ marginTop:10, padding:"8px 12px", background:BG, borderRadius:8 }}>
            <span style={{ fontSize:12, color:MUTED, fontFamily:KH }}>{t("ចំណងជើង:")} </span>
            <span style={{ fontSize:12, color:"var(--text)", fontFamily:KH }}>{selectedAnn.title}</span>
          </div>
        )}
      </div>

      {!selAnn ? (
        <div style={{ background:"var(--surface)", borderRadius:10, border:`1px solid ${BORDER}`,
                      padding:"40px 20px", textAlign:"center", color:MUTED,
                      fontSize:13, fontFamily:KH }}>
          {t("ជ្រើសសេចក្តីប្រកាសខាងលើ ដើម្បីមើលស្ថានភាពការអាន")}
        </div>
      ) : loading ? (
        <div style={{ background:"var(--surface)", borderRadius:10, border:`1px solid ${BORDER}`,
                      padding:"40px 20px", textAlign:"center", color:MUTED,
                      fontSize:13, fontFamily:KH }}>
          {t("កំពុងផ្ទុក...")}
        </div>
      ) : (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20 }}>
            {[
              { label:"អ្នកទទួលសរុប", value:total,  color:BROWN     },
              { label:"បានអាន",       value:read,   color:"#10B981" },
              { label:"មិនទាន់អាន",   value:unread, color:"#F59E0B" },
            ].map((s,i) => (
              <div key={i} style={{ background:"var(--surface)", borderRadius:10, padding:"14px 18px",
                                     border:`1px solid ${BORDER}` }}>
                <p style={{ margin:"0 0 6px", fontSize:12, color:MUTED, fontFamily:KH }}>{s.label}</p>
                <p style={{ margin:0, fontSize:26, fontWeight:700, color:s.color, fontFamily:MO }}>
                  {s.value}
                </p>
              </div>
            ))}
          </div>

          <div style={{ background:"var(--surface)", borderRadius:10, border:`1px solid ${BORDER}`, padding:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <span style={{ fontSize:12, fontWeight:600, color:MUTED, fontFamily:KH }}>{t("អត្រាការអាន")}</span>
              <span style={{ fontSize:12, color:BROWN, fontFamily:KH }}>{readRate}%</span>
            </div>
            <div style={{ height:10, borderRadius:20, background:BG, overflow:"hidden" }}>
              <div style={{ width:`${readRate}%`, height:"100%", background:"#10B981",
                            transition:"width .3s" }} />
            </div>

            {recipients.length > 0 ? (
              <table style={{ width:"100%", borderCollapse:"collapse", marginTop:18,
                              tableLayout:"fixed" }}>
                <colgroup>
                  <col style={{ width:"52%" }} />
                  <col style={{ width:"24%" }} />
                  <col style={{ width:"24%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <Th>{t("ឈ្មោះអ្នកទទួល")}</Th>
                    <Th>{t("ស្ថានភាពអាន")}</Th>
                    <Th>{t("ថ្ងៃអាន")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((r,i) => (
                    <tr key={r.officerId ?? r.no ?? i}
                      onMouseEnter={e => e.currentTarget.style.background=BG}
                      onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                      {/* read-status rows carry officerName; report rows carry receiverName */}
                      <td style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
                        <p style={{ fontSize:13, color:"var(--text)", fontFamily:KH }}>
                          {r.officerName || r.receiverName || r.officerFullNameKh || "—"}
                        </p>
                        {(r.receiverEmail || r.departmentName) && (
                          <p style={{ fontSize:11, color:MUTED, fontFamily:KH, marginTop:2,
                                      wordBreak:"break-word" }}>
                            {[r.departmentName, r.receiverEmail].filter(Boolean).join(" · ")}
                          </p>
                        )}
                      </td>
                      <Td>
                        <span style={{ display:"inline-flex", alignItems:"center", gap:5,
                                       padding:"4px 12px", borderRadius:20, fontSize:11,
                                       fontWeight:600, whiteSpace:"nowrap", lineHeight:1.6,
                                       background:r.isRead?"#D1FAE5":"#FEF3C7",
                                       color:r.isRead?"#065F46":"#92400E", fontFamily:KH }}>
                          {r.isRead
                            ? <MdCheckCircle size={13} style={{ flexShrink:0 }} />
                            : <MdHourglassEmpty size={13} style={{ flexShrink:0 }} />}
                          {r.readStatus || (r.isRead ? "អានរួច" : "មិនទាន់អាន")}
                        </span>
                      </Td>
                      <Td muted>{r.readAt ? toDisplay(r.readAt) : "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              // read-status returns `recipients: null` — only the totals are available
              <p style={{ marginTop:18, fontSize:12, color:MUTED, fontFamily:KH, lineHeight:1.7 }}>
                {total > 0
                  ? "ប្រព័ន្ធផ្តល់តែចំនួនសរុបប៉ុណ្ណោះ — មិនមានបញ្ជីឈ្មោះអ្នកទទួលលម្អិតទេ។"
                  : "មិនទាន់មានអ្នកទទួលសម្រាប់សេចក្តីប្រកាសនេះ។"}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function Announcements({ defaultTab = "list" }) {
  return (
    <div>
      {defaultTab === "list"       && <AnnouncementList />}
      {defaultTab === "recipients" && <Recipients />}
    </div>
  );
}